import { Link } from '@tanstack/react-router';
import { useEffect, useState, type ReactNode } from 'react';
import type { LobbyPayload, TimePreset } from '@ccc/shared-types';
import { Panel } from '../components/Card';
import { Button } from '../components/Button';
import { useAuthStore } from '../lib/auth';
import { apiJson, loadLobby } from '../lib/multiplayer';

const TIME_PRESETS: TimePreset[] = [
  { label: '3 + 0', initialSeconds: 180, incrementSeconds: 0 },
  { label: '5 + 3', initialSeconds: 300, incrementSeconds: 3 },
  { label: '10 + 0', initialSeconds: 600, incrementSeconds: 0 },
  { label: '15 + 10', initialSeconds: 900, incrementSeconds: 10 },
];

export function PlayRoute() {
  const user = useAuthStore((s) => s.user);
  const [lobby, setLobby] = useState<LobbyPayload | null>(null);
  const [time, setTime] = useState(TIME_PRESETS[1]!);
  const [visibility, setVisibility] = useState<'public' | 'private'>('public');
  const [rated, setRated] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);

  async function refresh() {
    if (!user) return;
    try {
      setLobby(await loadLobby());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the lobby.');
    }
  }

  useEffect(() => {
    void refresh();
    const id = window.setInterval(() => void refresh(), 4000);
    return () => window.clearInterval(id);
  }, [user?.id]);

  async function createChallenge() {
    setBusy('create');
    setError(null);
    try {
      const data = await apiJson<{ gameId: string; gameUrl: string; inviteUrl: string | null }>(
        '/api/challenges',
        {
          method: 'POST',
          body: JSON.stringify({
            initialSeconds: time.initialSeconds,
            incrementSeconds: time.incrementSeconds,
            visibility,
            rated,
          }),
        },
      );
      if (data.inviteUrl) {
        setInviteUrl(data.inviteUrl);
      } else {
        await refresh();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create that challenge.');
    } finally {
      setBusy(null);
    }
  }

  async function acceptChallenge(id: string) {
    setBusy(id);
    try {
      const data = await apiJson<{ gameUrl: string }>(`/api/challenges/${id}/accept`, {
        method: 'POST',
        body: '{}',
      });
      window.location.assign(data.gameUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not accept that challenge.');
      await refresh();
    } finally {
      setBusy(null);
    }
  }

  async function quickPair(action: 'enqueue' | 'cancel') {
    setBusy(`quick-${action}`);
    setError(null);
    try {
      const data = await apiJson<{ status: string; gameId?: string }>(`/api/quick-pair/${action}`, {
        method: 'POST',
        body:
          action === 'enqueue'
            ? JSON.stringify({
                initialSeconds: time.initialSeconds,
                incrementSeconds: time.incrementSeconds,
                rated,
              })
            : '{}',
      });
      if (data.gameId) window.location.assign(`/game/${data.gameId}`);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Quick pair is not available.');
    } finally {
      setBusy(null);
    }
  }

  if (!user) {
    return (
      <div className="h-full w-full max-w-[900px] mx-auto px-5 md:px-10 py-6 grid place-items-center">
        <Panel className="p-6 text-center max-w-[520px]">
          <h1 className="font-display text-3xl font-bold">Play people with an account</h1>
          <p className="mt-2 text-sm text-ink-muted">
            Guest games are bot-only. Join or log in to create challenges, enter the lobby,
            and play other players.
          </p>
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            <Link to="/join">
              <Button>Join now</Button>
            </Link>
            <Link to="/login">
              <Button variant="secondary">Log in</Button>
            </Link>
            <Link to="/play/bot">
              <Button variant="ghost">Play a bot</Button>
            </Link>
          </div>
        </Panel>
      </div>
    );
  }

  const quick = lobby?.quickPair;

  return (
    <div className="min-h-full w-full max-w-[1400px] mx-auto px-3 md:px-8 py-4 pb-12 flex flex-col gap-4">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold">Lobby</h1>
          <p className="text-sm text-ink-muted">Create, pair, resume, or watch a live game.</p>
        </div>
        <Link to="/play/bot">
          <Button variant="secondary">Play a bot</Button>
        </Link>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[380px_minmax(0,1fr)] gap-4">
        <div className="grid grid-rows-[auto_auto_auto] gap-4">
          <Panel className="p-4">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-ink-muted">Create challenge</h2>
            <TimePicker time={time} setTime={setTime} />
            <div className="mt-3 grid grid-cols-2 gap-2">
              <Toggle active={visibility === 'public'} onClick={() => setVisibility('public')}>
                Public
              </Toggle>
              <Toggle active={visibility === 'private'} onClick={() => setVisibility('private')}>
                Private link
              </Toggle>
            </div>
            <label className="mt-3 flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={rated && visibility === 'public'}
                disabled={visibility === 'private'}
                onChange={(e) => setRated(e.target.checked)}
              />
              Rated
            </label>
            <Button className="mt-3 w-full" onClick={createChallenge} disabled={busy === 'create'}>
              {visibility === 'private' ? 'Make invite' : 'Create challenge'}
            </Button>
            {inviteUrl && (
              <div className="mt-3 rounded-md border border-ink/10 bg-ink/[0.03] p-3 text-xs">
                <div className="font-semibold text-ink">Private link ready</div>
                <button
                  type="button"
                  className="mt-1 text-left text-accent break-all hover:underline"
                  onClick={() => void navigator.clipboard?.writeText(inviteUrl)}
                >
                  {inviteUrl}
                </button>
              </div>
            )}
          </Panel>

          <Panel className="p-4">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-ink-muted">Quick pair</h2>
            <p className="mt-1 text-sm text-ink-muted">
              {quick?.status === 'waiting'
                ? `Waiting for ${clockLabel(quick.initialSeconds ?? time.initialSeconds, quick.incrementSeconds ?? time.incrementSeconds)}.`
                : quick?.status === 'matched'
                  ? 'Pair found.'
                  : 'Find someone with the same clock.'}
            </p>
            <div className="mt-3 flex gap-2">
              {quick?.status === 'matched' && quick.gameId ? (
                <Button className="flex-1" onClick={() => window.location.assign(`/game/${quick.gameId}`)}>
                  Open game
                </Button>
              ) : quick?.status === 'waiting' ? (
                <Button
                  className="flex-1"
                  variant="secondary"
                  onClick={() => quickPair('cancel')}
                  disabled={busy === 'quick-cancel'}
                >
                  Cancel
                </Button>
              ) : (
                <Button
                  className="flex-1"
                  onClick={() => quickPair('enqueue')}
                  disabled={busy === 'quick-enqueue'}
                >
                  Start pairing
                </Button>
              )}
            </div>
          </Panel>

          <Panel className="p-4 flex flex-col">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-ink-muted shrink-0">
              Your games
            </h2>
            <div className="mt-3 max-h-[420px] overflow-auto space-y-2">
              {lobby?.myGames.length ? (
                lobby.myGames.map((game) => (
                  <GameRow key={game.id} title={game.opponentName} meta={gameMeta(game)} to={`/game/${game.id}`} />
                ))
              ) : (
                <EmptyLine text="No active games yet." />
              )}
            </div>
          </Panel>
        </div>

        <div className="grid grid-rows-[auto_auto] gap-4">
          <Panel className="p-4 flex flex-col">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-ink-muted shrink-0">
              Open challenges
            </h2>
            <div className="mt-3 max-h-[420px] overflow-auto space-y-2">
              {lobby?.challenges.length ? (
                lobby.challenges.map((challenge) => (
                  <div
                    key={challenge.id}
                    className="flex items-center justify-between gap-3 rounded-md border border-ink/10 p-3"
                  >
                    <div>
                      <div className="font-semibold">{challenge.creatorName}</div>
                      <div className="text-xs text-ink-muted">
                        {clockLabel(challenge.initialSeconds, challenge.incrementSeconds)} ·{' '}
                        {challenge.rated ? 'Rated' : 'Casual'} · plays {challenge.creatorSide}
                      </div>
                    </div>
                    {challenge.creatorId === user.id ? (
                      <span className="text-xs text-ink-muted">Yours</span>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => acceptChallenge(challenge.id)}
                        disabled={busy === challenge.id}
                      >
                        Accept
                      </Button>
                    )}
                  </div>
                ))
              ) : (
                <EmptyLine text="No public challenges right now." />
              )}
            </div>
          </Panel>

          <Panel className="p-4 flex flex-col">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-ink-muted shrink-0">
              Watch live
            </h2>
            <div className="mt-3 max-h-[420px] overflow-auto space-y-2">
              {lobby?.liveGames.length ? (
                lobby.liveGames.map((game) => (
                  <GameRow
                    key={game.id}
                    title={`${game.whiteName} vs ${game.blackName}`}
                    meta={gameMeta(game)}
                    to={`/game/${game.id}`}
                  />
                ))
              ) : (
                <EmptyLine text="Live public games will appear here." />
              )}
            </div>
          </Panel>
        </div>
      </div>
      {error && <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:w-[420px] rounded-md bg-danger text-white px-4 py-3 text-sm shadow-card">{error}</div>}
    </div>
  );
}

function TimePicker({ time, setTime }: { time: TimePreset; setTime: (time: TimePreset) => void }) {
  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {TIME_PRESETS.map((preset) => (
        <Toggle
          key={preset.label}
          active={time.label === preset.label}
          onClick={() => setTime(preset)}
          mono
        >
          {preset.label}
        </Toggle>
      ))}
    </div>
  );
}

function Toggle({
  active,
  onClick,
  children,
  mono,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
  mono?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`h-10 px-3 rounded-md border text-sm transition focus-ring ${
        active ? 'border-accent bg-accent/10 text-ink' : 'border-ink/10 text-ink-muted hover:bg-ink/[0.04]'
      } ${mono ? 'font-mono' : 'font-medium'}`}
    >
      {children}
    </button>
  );
}

function GameRow({ title, meta, to }: { title: string; meta: string; to: string }) {
  return (
    <a
      href={to}
      className="flex items-center justify-between gap-3 rounded-md border border-ink/10 p-3 hover:bg-ink/[0.04] transition focus-ring"
    >
      <div className="min-w-0">
        <div className="font-semibold truncate">{title}</div>
        <div className="text-xs text-ink-muted">{meta}</div>
      </div>
      <span className="text-xs font-semibold text-accent">Open</span>
    </a>
  );
}

function EmptyLine({ text }: { text: string }) {
  return <div className="h-24 grid place-items-center text-sm text-ink-muted">{text}</div>;
}

function clockLabel(initialSeconds: number, incrementSeconds: number) {
  return `${Math.floor(initialSeconds / 60)} + ${incrementSeconds}`;
}

function gameMeta(game: { initialSeconds: number; incrementSeconds: number; rated: boolean; status: string }) {
  return `${clockLabel(game.initialSeconds, game.incrementSeconds)} · ${game.rated ? 'Rated' : 'Casual'} · ${game.status.replace('_', ' ')}`;
}
